import React, { useEffect, useMemo, useState } from 'react'
import { Plus, CreditCard, CheckCircle, Clock, XCircle, DollarSign } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import Breadcrumb from '../../components/Common/Breadcrumb'
import DataTable from '../../components/DataTable/DataTable'
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal'
import subscriptionPaymentService from '../../services/subscriptionPaymentService'
import {
  defaultPaymentStats,
  extractPayments,
  formatCurrency,
  formatDate,
  getPaymentStatusClass,
  transformPaymentRow
} from './subscriptionUtils'
import '../../styles/subscriptions-tables.css'

const PaymentList = () => {
  const navigate = useNavigate()
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState(defaultPaymentStats())
  const [deleteItem, setDeleteItem] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      setLoading(true)
      const [listRes, statsRes] = await Promise.all([
        subscriptionPaymentService.getAll({ limit: 500 }),
        subscriptionPaymentService.getStats()
      ])
      if (listRes.success) {
        setData(extractPayments(listRes).map(transformPaymentRow))
      } else {
        toast.error(listRes.message || 'Failed to load payments')
      }
      if (statsRes.success && statsRes.data) {
        setStats({ ...defaultPaymentStats(), ...statsRes.data })
      }
    } catch (error) {
      console.error('Error loading subscription payments:', error)
      if (!error._errorHandled) toast.error('Failed to load payments')
    } finally {
      setLoading(false)
    }
  }

  const subscriptionLabel = (row) => {
    const sub = row.subscription
    if (!sub) return '-'
    if (typeof sub === 'object') {
      return sub.subscriptionName || sub.subscriptionNumber || '-'
    }
    return String(sub)
  }

  const columns = useMemo(() => [
    {
      accessorKey: 'paymentNumber',
      header: 'Payment #',
      cell: ({ getValue }) => <span className="code-text">{getValue() || '-'}</span>
    },
    {
      accessorKey: 'subscription',
      header: 'Subscription',
      cell: ({ row }) => subscriptionLabel(row.original)
    },
    {
      accessorKey: 'paymentDate',
      header: 'Payment Date',
      meta: { align: 'center' },
      cell: ({ getValue }) => formatDate(getValue())
    },
    {
      accessorKey: 'amount',
      header: 'Amount',
      meta: { align: 'right' },
      cell: ({ getValue, row }) => (
        <div className={`sub-cost${Number(getValue()) ? '' : ' sub-cost--zero'}`}>
          <span className="sub-cost__main">{formatCurrency(getValue(), row.original.currency)}</span>
        </div>
      )
    },
    {
      accessorKey: 'status',
      header: 'Status',
      meta: { align: 'center' },
      cell: ({ getValue }) => (
        <span className={`sub-status ${getPaymentStatusClass(getValue())}`}>
          {getValue() || '-'}
        </span>
      )
    },
    {
      accessorKey: 'paymentMethod',
      header: 'Method',
      meta: { align: 'center' },
      cell: ({ getValue }) => getValue() || '-'
    },
    {
      accessorKey: 'reference',
      header: 'Reference',
      cell: ({ getValue }) => getValue() || '-'
    }
  ], [])

  const breadcrumbItems = [
    { label: 'Subscriptions', path: '/subscriptions', onClick: () => navigate('/subscriptions') },
    { label: 'Transactions', path: '/subscriptions', onClick: () => navigate('/subscriptions') },
    { label: 'Payments', isCurrent: true }
  ]

  const handleAdd = () => navigate('/subscriptions/payments/new')
  const handleView = (row) => navigate(`/subscriptions/payments/view/${row.id}`)
  const handleEdit = (row) => navigate(`/subscriptions/payments/edit/${row.id}`)
  const handleDelete = (row) => {
    setDeleteItem(row)
    setShowDeleteModal(true)
  }

  const confirmDelete = async () => {
    if (!deleteItem) return
    try {
      setLoading(true)
      const response = await subscriptionPaymentService.remove(deleteItem.id)
      if (response.success) {
        toast.success('Payment deleted')
        loadData()
      } else {
        toast.error(response.message || 'Failed to delete payment')
      }
    } catch (error) {
      console.error('Delete payment error:', error)
      if (!error._errorHandled) toast.error('Failed to delete payment')
    } finally {
      setShowDeleteModal(false)
      setDeleteItem(null)
      setLoading(false)
    }
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      <div className="page-header">
        <div className="page-title">
          <h1>Subscription Payments</h1>
          <p>Record and track subscription billing payments</p>
        </div>
        <div className="page-actions">
          <button type="button" className="btn-primary" onClick={handleAdd}>
            <Plus size={16} />
            New Payment
          </button>
        </div>
      </div>

      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon"><CreditCard size={16} /></div>
            <div className="metric-trend positive"><span>Total</span></div>
          </div>
          <div className="metric-value">{stats.total}</div>
          <div className="metric-title">All Payments</div>
          <div className="metric-subtitle">Recorded transactions</div>
        </div>
        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon"><CheckCircle size={16} /></div>
            <div className="metric-trend positive"><span>Paid</span></div>
          </div>
          <div className="metric-value">{stats.paid}</div>
          <div className="metric-title">Paid</div>
          <div className="metric-subtitle">Completed</div>
        </div>
        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon"><Clock size={16} /></div>
            <div className="metric-trend neutral"><span>Pending</span></div>
          </div>
          <div className="metric-value">{stats.pending}</div>
          <div className="metric-title">Pending</div>
          <div className="metric-subtitle">Awaiting settlement</div>
        </div>
        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon"><XCircle size={16} /></div>
            <div className="metric-trend negative"><span>Failed</span></div>
          </div>
          <div className="metric-value">{stats.failed}</div>
          <div className="metric-title">Failed / Refunded</div>
          <div className="metric-subtitle">{stats.refunded} refunded</div>
        </div>
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon"><DollarSign size={16} /></div>
            <div className="metric-trend positive"><span>Paid</span></div>
          </div>
          <div className="metric-value">{formatCurrency(stats.totalAmount, 'INR')}</div>
          <div className="metric-title">Total Paid Amount</div>
          <div className="metric-subtitle">Successful payments</div>
        </div>
      </div>

      <DataTable
        data={data}
        columns={columns}
        loading={loading}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
        noCard={true}
        pageSize={10}
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => { setShowDeleteModal(false); setDeleteItem(null) }}
        onConfirm={confirmDelete}
        title="Delete Payment"
        message={`Delete payment "${deleteItem?.paymentNumber}"?`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />
    </div>
  )
}

export default PaymentList
